import { BarChart2, CheckCircle2, TrendingDown, TrendingUp, XCircle } from 'lucide-react';

import { formatNumber } from '@/lib/format';
import type { BroadcastMetrics, MetricTrend } from '@/types/messaging';

interface NotificationMetricCardsProps {
  metrics: BroadcastMetrics | null;
  isLoading: boolean;
}

/**
 * How a figure has moved since the same point last month.
 *
 * The server counts last month only as far as this month has run, so the
 * comparison is like for like. A percentage where there is something to
 * compare against, and a plain line where there is not: a change against zero
 * is a division by nothing.
 */
function trendOf(trend: MetricTrend | undefined, noun: string): { text: string; isUp: boolean } {
  if (!trend) return { text: '—', isUp: true };

  const { value, previous } = trend;

  if (previous === 0) {
    return {
      text: value === 0 ? `No ${noun}, as at this point last month` : 'None by this point last month',
      isUp: value >= 0,
    };
  }

  const change = ((value - previous) / previous) * 100;
  const rounded = Math.abs(Math.round(change * 10) / 10);

  return {
    text: `${rounded}% vs last month to date`,
    isUp: change >= 0,
  };
}

/**
 * Percentage points, not a percentage of a percentage — and only where both
 * periods sent something, since a rate over no messages is not 0%.
 */
function rateTrendOf(
  rate: MetricTrend | undefined,
  messages: MetricTrend | undefined,
): { text: string; isUp: boolean } {
  if (!rate || !messages || messages.value === 0) return { text: '—', isUp: true };
  if (messages.previous === 0) return { text: 'No sends by this point last month', isUp: true };

  const change = Math.round((rate.value - rate.previous) * 10) / 10;
  return { text: `${Math.abs(change)} pts vs last month to date`, isUp: change >= 0 };
}

/** An em dash until the figures arrive, so the tiles never show a stale number. */
function show(value: number | undefined, isLoading: boolean, suffix = ''): string {
  if (isLoading || value === undefined) return '—';
  return `${formatNumber(value)}${suffix}`;
}

export function NotificationMetricCards({ metrics, isLoading }: NotificationMetricCardsProps) {
  const broadcasts = trendOf(metrics?.broadcasts, 'broadcasts');
  const messages = trendOf(metrics?.messages, 'messages');
  const rate = rateTrendOf(metrics?.deliveryRate, metrics?.messages);
  // Nothing sent this month has no delivery rate — the server's 0 would read
  // as every message failing.
  const hasSentThisMonth = (metrics?.messages.value ?? 0) > 0;
  const failed = trendOf(metrics?.failed, 'failures');

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-full">
      {/* Metric 1: Broadcasts sent this month.

          This tile used to read "SMS Balance — 48,520 credits". There is no
          such number to read: the institute sends through its own provider
          account and this product has no sight of what is left in it, so the
          tile now carries the one thing it can say truthfully about volume that
          the tile beside it does not — how many separate announcements went
          out, rather than how many handsets were reached. */}
      <div className="rounded-2xl border border-[#ffe8d6] dark:border-[#472912] bg-[#fff8f2] dark:bg-[#26170c] p-4 flex flex-col justify-between shadow-md shadow-orange-500/10 hover:shadow-xl hover:shadow-orange-500/20 hover:-translate-y-0.5 transition-all duration-200 min-w-0 w-full">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#f97316] text-white flex items-center justify-center shadow-md shadow-orange-500/30 shrink-0">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5 text-white"
            >
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs sm:text-[14px] font-bold text-[#9a3412] dark:text-orange-200 block leading-tight">
              Broadcasts Sent
            </span>
            <span className="font-black text-[#431407] dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {show(metrics?.broadcasts.value, isLoading)}
            </span>
            <span className="text-[11px] sm:text-xs font-bold text-[#9a3412]/70 dark:text-slate-400 block mt-0.5">
              This month
            </span>
          </div>
        </div>
        <div
          className={`border-t border-[#ffe8d6] dark:border-slate-800/80 pt-2 mt-2.5 flex items-center gap-1.5 text-[11.5px] font-extrabold ${
            broadcasts.isUp
              ? 'text-[#00875a] dark:text-emerald-400'
              : 'text-rose-600 dark:text-rose-400'
          }`}
        >
          {broadcasts.isUp ? (
            <TrendingUp className="h-3.5 w-3.5" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" />
          )}
          <span>{broadcasts.text}</span>
        </div>
      </div>

      {/* Metric 2: Sent This Month */}
      <div className="rounded-2xl border border-[#dcf3e4] dark:border-[#173e2a] bg-[#f2faf5] dark:bg-[#0d2117] p-4 flex flex-col justify-between shadow-md shadow-emerald-500/10 hover:shadow-xl hover:shadow-emerald-500/20 hover:-translate-y-0.5 transition-all duration-200 min-w-0 w-full">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#10b981] to-[#34d399] text-white flex items-center justify-center shadow-md shadow-emerald-500/30 shrink-0">
            <BarChart2 className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs sm:text-[14px] font-bold text-emerald-900 dark:text-emerald-200 block leading-tight">
              Sent This Month
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {show(metrics?.messages.value, isLoading)}
            </span>
          </div>
        </div>
        <div
          className={`border-t border-[#dcf3e4]/80 dark:border-[#173e2a]/80 pt-2 mt-2.5 flex items-center gap-1.5 text-[11.5px] font-extrabold ${
            messages.isUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
          }`}
        >
          {messages.isUp ? (
            <TrendingUp className="h-3.5 w-3.5" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" />
          )}
          <span>{messages.text}</span>
        </div>
      </div>

      {/* Metric 3: Delivery Rate */}
      <div className="rounded-2xl border border-[#f3e8ff] dark:border-[#381a54] bg-[#faf5ff] dark:bg-[#1f102e] p-4 flex flex-col justify-between shadow-md shadow-purple-500/10 hover:shadow-xl hover:shadow-purple-500/20 hover:-translate-y-0.5 transition-all duration-200 min-w-0 w-full">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#a855f7] to-[#c084fc] text-white flex items-center justify-center shadow-md shadow-purple-500/30 shrink-0">
            <CheckCircle2 className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs sm:text-[14px] font-bold text-purple-900 dark:text-purple-200 block leading-tight">
              Delivery Rate
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {hasSentThisMonth ? show(metrics?.deliveryRate.value, isLoading, '%') : '—'}
            </span>
            <span className="text-[11px] sm:text-xs font-bold text-purple-900/70 dark:text-slate-400 block mt-0.5">
              This month
            </span>
          </div>
        </div>
        <div
          className={`border-t border-[#f3e8ff]/80 dark:border-[#381a54]/80 pt-2 mt-2.5 flex items-center gap-1.5 text-[11.5px] font-extrabold ${
            rate.isUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
          }`}
        >
          {rate.isUp ? (
            <TrendingUp className="h-3.5 w-3.5" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" />
          )}
          <span>{rate.text}</span>
        </div>
      </div>

      {/* Metric 4: Failed messages.

          Down is good here, so the arrow is inverted against the other three:
          fewer failures than last month is the improvement. */}
      <div className="rounded-2xl border border-[#ffdce6] dark:border-[#47152c] bg-[#fff2f6] dark:bg-[#280e1b] p-4 flex flex-col justify-between shadow-md shadow-rose-500/10 hover:shadow-xl hover:shadow-rose-500/20 hover:-translate-y-0.5 transition-all duration-200 min-w-0 w-full">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#f43f5e] to-[#fb7185] text-white flex items-center justify-center shadow-md shadow-rose-500/30 shrink-0">
            <XCircle className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs sm:text-[14px] font-bold text-rose-900 dark:text-rose-200 block leading-tight">
              Failed Messages
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {show(metrics?.failed.value, isLoading)}
            </span>
            <span className="text-[11px] sm:text-xs font-bold text-rose-900/70 dark:text-slate-400 block mt-0.5">
              This month
            </span>
          </div>
        </div>
        <div
          className={`border-t border-[#ffdce6]/80 dark:border-[#47152c]/80 pt-2 mt-2.5 flex items-center gap-1.5 text-[11.5px] font-extrabold ${
            failed.isUp ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
          }`}
        >
          {failed.isUp ? (
            <TrendingUp className="h-3.5 w-3.5" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" />
          )}
          <span>{failed.text}</span>
        </div>
      </div>
    </div>
  );
}
